/**
 * Sign-in for everyone: email + password, plus passkeys. NO magic link (owner, 2026-11-03:
 * "remove the login by one-time-email. A user can log in by email/password or also use a
 * passkey later on"). better-auth owns `/api/auth/*`; this block is binding for API and client.
 *
 * Methods:
 * - Email + password for every account (divers and staff). Password 12–128 chars
 *   (`PASSWORD_MIN_LENGTH` / `PASSWORD_MAX_LENGTH`), never trimmed.
 * - Passkeys for every account, staff included. A signed-in user adds, lists and deletes them on
 *   the account page (enrolment needs a fresh session, better-auth default). Every add and
 *   delete is audited (entity user, `{ passkeys: { from: <count>, to: <count> } }`), and an add
 *   sends a "Passkey hinzugefügt" notice mail.
 * - Promoting a user to moderator or admin (PATCH /api/admin/users/:id role) deletes that
 *   user's passkeys AND sessions in the same transaction (audited `passkeys` as above), so a
 *   staff session always starts with a fresh staff sign-in.
 * - The magic-link plugin, its routes and its mails are removed. Old links → 404; nothing signs
 *   in from an email link. `user.changeEmail` stays disabled (its verify step signs in).
 *
 * Sign-up (divers): `POST /api/auth/sign-up/email` with name (1–120), email, password.
 * - Always role "diver" (a body with `role` → 400), as before. Audited: entity user, action
 *   create, actor = the new user, `{ after }` (redacted on account deletion like any user row).
 * - Gated exactly like before: registration switch + hourly cap (`SignupPolicy` and the DB
 *   trigger), per-IP and per-email rate limits. With registration closed or over the cap, an
 *   unknown email creates nothing and sends nothing.
 * - No enumeration: sign-up answers the same 200 for a new email, an existing one and a refused
 *   one, and never faster than a fixed floor (with jitter), so timing does not tell them apart
 *   (same for the reset request and the verification resend). A policy refusal (the fast-path
 *   hook AND the DB trigger wrapper) THROWS a 403 APIError, never returns false/null, so
 *   better-auth answers its generic 200 (returning false yields a distinguishable 400). All
 *   mails are fire-and-forget (`void mailer.…`), never awaited, so response times do not depend
 *   on whether a mail is sent. An existing address gets no new account; a diver's address gets
 *   an "already registered" mail with the reset link, a staff address gets no mail at all;
 *   never a sign-in.
 * - `requireEmailVerification`: the account exists unverified and CANNOT sign in until the
 *   confirmation link (a signed link valid 24 h; opening it again just re-verifies) is opened.
 *   The link verifies only; it does not sign in. The client then shows "E-Mail bestätigt, bitte
 *   anmelden". An unverified sign-in with the right password resends the confirmation and
 *   answers `email_not_verified`; a wrong password gives the generic invalid-credentials answer.
 * - Confirmation resends (the sign-in resend AND the public `POST /api/auth/send-verification-
 *   email`) are rate-limited per IP and per email like the reset request.
 *
 * Password reset ("Passwort vergessen?"), also the way existing passwordless divers set their
 * first password. DIVERS ONLY: staff get no credential by email (a compromised mailbox must not
 * become a staff login, as with the old magic link). For a staff address the request answers
 * the same 200 and sends nothing; an admin resets staff with `set-password --password-stdin`.
 * - `POST /api/auth/request-password-reset` {email, redirectTo}: always the same 200; a mail
 *   only for an existing, not banned diver (filtered in `sendResetPassword`). Rate-limited per
 *   IP and per email.
 * - The link (one use, 30 min) opens the client's "Neues Passwort" page; the new password is
 *   set with `POST /api/auth/reset-password`. It never creates a session: the user then signs
 *   in normally. All sessions AND all passkeys of that user are deleted (a reset means the old
 *   credentials may be compromised; passkeys are added again after signing in).
 * - In `onPasswordReset`: mark the email verified (the mailbox was proven), write the audit row
 *   (entity user, update, `{ password: { from: null, to: "changed" } }` plus `passkeys` when any
 *   were deleted, actor = the user). better-auth runs this hook after the password update, not
 *   in one transaction: an accepted exception to the same-transaction audit rule (the other
 *   is the sign-up `user create` row, written in better-auth's user `after` hook).
 * - Change password (account page): current password required, `revokeOtherSessions: true`
 *   (this session stays, the others end), audited like the reset.
 *
 * Existing data: accounts keep their ids. The migration DELETES every existing passkey (owner,
 * 2026-11-03: "you can delete existing keys, there shouldn't be any on prod"); users add new ones
 * after signing in with a password. A diver without a password uses the reset flow once.
 * Existing divers count as verified (they signed up through an email link): the migration sets
 * `email_verified = true` for existing accounts and deletes the passkeys, nothing else.
 *
 * Mails (de/en/fr/it, default de): the locale is the `/<lang>/` prefix of the request's
 * `callbackURL` / `redirectTo` (which the client always sends), else de. Confirmation, password
 * reset, already-registered, passkey added. Tokens appear only in the link; analytics never send
 * them (the tracker drops query strings and hashes, and the verify/reset pages are untracked).
 *
 * Client pages (one form for divers and staff):
 * - Sign-in: "Mit Passkey anmelden" (when WebAuthn is available), then email + password,
 *   "Anmelden", links "Passwort vergessen?" and, while registration is open, "Konto erstellen".
 *   Admin login uses the same form.
 * - Sign-up, check-your-mail, email-confirmed, reset request, reset set (new password twice),
 *   account page: change password, passkeys (all roles).
 * - Error codes the client must word: invalid credentials (one message for unknown email and
 *   wrong password), `email_not_verified`, banned, rate limited (with Retry-After), network.
 */
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

/** Link lifetimes in seconds. */
export const EMAIL_VERIFICATION_TTL_S = 24 * 60 * 60;
export const PASSWORD_RESET_TTL_S = 30 * 60;

/** Client routes the API's mail links point to (under /<lang>/). */
export const AUTH_LINK_PATHS = {
  verified: "login/bestaetigt",
  resetPassword: "login/neues-passwort",
} as const;

/** True when `pw` has an acceptable length (the API enforces the same bounds). */
export function passwordLengthOk(pw: string): boolean {
  return pw.length >= PASSWORD_MIN_LENGTH && pw.length <= PASSWORD_MAX_LENGTH;
}
